import React, { ReactElement } from 'react';
import { useHistory } from 'react-router-dom';

import { Row, Col, message } from 'antd';
import { I18n } from '@aws-amplify/core';

import { useKimLhc } from '@/context/KimLhc';

import { PreviousCol } from './styles';
import { GhostButton, PrimaryButton } from '@/components/ui/Button';

export interface FooterProps {
	currentStep: number;
	totalSteps: number;
	onPrevious?: () => void | Promise<void>;
	onClose?: () => void | Promise<void>;
	isLoading?: boolean;
}

interface FooterLayoutProps {
	children: ReactElement | ReactElement[];
	centered?: boolean;
}

function FooterLayout({ children, centered = false }: FooterLayoutProps) {
	return (
		<Row>
			<Col xs={24} style={{ marginTop: '2rem' }}>
				<Row justify="center" align={centered ? 'middle' : undefined} gutter={[0, 8]}>
					{children}
				</Row>
			</Col>
		</Row>
	);
}

export function Footer({ currentStep, onPrevious, isLoading = false, totalSteps }: Readonly<FooterProps>): JSX.Element {
	const history = useHistory();
	const { form } = useKimLhc();

	function handleNextStep() {
		form.submit();
	}

	async function handlePreviousStep() {
		try {
			if (onPrevious) {
				await onPrevious();
			}
		} catch (error) {
			message.error(I18n.get('Error returning to previous step'));
		}
	}

	const isFirstStep = currentStep === 0;

	const isLastStep = currentStep === totalSteps - 1;

	function handleFinish() {
		history.push('/reporting');
	}

	if (isLastStep) {
		return (
			<FooterLayout centered>
				<PreviousCol>
					<GhostButton onClick={handlePreviousStep} loading={isLoading}>
						{I18n.get('Previous')}
					</GhostButton>
				</PreviousCol>

				<Col>
					<PrimaryButton onClick={handleFinish} loading={isLoading}>
						{I18n.get('Finish')}
					</PrimaryButton>
				</Col>
			</FooterLayout>
		);
	}

	if (!isFirstStep) {
		return (
			<FooterLayout centered>
				<PreviousCol>
					<GhostButton onClick={handlePreviousStep} loading={isLoading}>
						{I18n.get('Previous')}
					</GhostButton>
				</PreviousCol>
				<Col>
					<PrimaryButton onClick={handleNextStep} loading={isLoading}>
						{I18n.get('Next')}
					</PrimaryButton>
				</Col>
			</FooterLayout>
		);
	}

	return (
		<FooterLayout>
			<Col>
				<PrimaryButton onClick={handleNextStep} loading={isLoading}>
					{I18n.get('Next')}
				</PrimaryButton>
			</Col>
		</FooterLayout>
	);
}
